import React, { useState } from "react"
import {
  Image,
  Button,
  Popconfirm,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  Tooltip,
  Grid,
  Empty,
  Flex,
  Checkbox,
} from "antd"
import {
  getApiUrlGasProjectFiles,
  getApiUrlGasProjectFilesWithFileNumber,
  getUrlGasProjectFiles,
} from "common/global"

import "./GridFilesAnt.scss"
import { deleteGasProjectFile } from "store/actions"
import { connect } from "react-redux"
import {
  DeleteOutlined,
  DownloadOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileZipOutlined,
  FileUnknownOutlined,
  EyeOutlined,
  CloudDownloadOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons"

const { Text, Title } = Typography
const { useBreakpoint } = Grid

// Get file icon based on extension
const getFileIcon = (fileName, size = 36) => {
  const ext = fileName?.split(".").pop()?.toLowerCase()
  const iconStyle = { fontSize: size }

  switch (ext) {
    case "pdf":
      return <FilePdfOutlined style={{ ...iconStyle, color: "#ff4d4f" }} />
    case "doc":
    case "docx":
      return <FileWordOutlined style={{ ...iconStyle, color: "#1890ff" }} />
    case "xls":
    case "xlsx":
      return <FileExcelOutlined style={{ ...iconStyle, color: "#52c41a" }} />
    case "zip":
    case "rar":
    case "7z":
      return <FileZipOutlined style={{ ...iconStyle, color: "#faad14" }} />
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "webp":
      return <FileImageOutlined style={{ ...iconStyle, color: "#722ed1" }} />
    default:
      return <FileUnknownOutlined style={{ ...iconStyle, color: "#8c8c8c" }} />
  }
}

// Check if file is an image
const isImageFile = fileName => {
  const ext = fileName?.split(".").pop()?.toLowerCase()
  return ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext)
}

// Get file type color
const getFileTypeColor = fileName => {
  const ext = fileName?.split(".").pop()?.toLowerCase()
  switch (ext) {
    case "pdf":
      return "red"
    case "doc":
    case "docx":
      return "blue"
    case "xls":
    case "xlsx":
      return "green"
    case "zip":
    case "rar":
    case "7z":
      return "orange"
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "webp":
      return "purple"
    default:
      return "default"
  }
}

export const renderDeleteButton = (params, props, showDelete) => {
  return showDelete ? (
    <Popconfirm
      title="حذف فایل"
      description="آیا از حذف این فایل اطمینان دارید؟"
      onConfirm={() => props.onDeleteGasProjectFile(params.id)}
      okText="بله"
      cancelText="خیر"
      placement="topRight"
    >
      <Button
        danger
        type="text"
        icon={<DeleteOutlined />}
        size="small"
        style={{ borderRadius: 6 }}
      />
    </Popconfirm>
  ) : null
}

// File Card Component (for both mobile and desktop)
const FileCard = ({ file, props, showDelete, t, isMobile }) => {
  const fileUrl = getApiUrlGasProjectFilesWithFileNumber(
    file.folderName + "/" + file.fileName,
    file.gasProject.fileNumber
  )
  const isImage = isImageFile(file.fileName)
  const ext = file.fileName?.split(".").pop()?.toUpperCase()

  return (
    <Card
      hoverable
      size="small"
      style={{
        borderRadius: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        border: "1px solid #f0f0f0",
        overflow: "hidden",
        height: "100%",
      }}
      bodyStyle={{ padding: 0 }}
      className="file-card-hover"
    >
      {/* Preview Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          height: isMobile ? 100 : 120,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {isImage ? (
          <Image
            height="100%"
            width="100%"
            src={fileUrl}
            style={{
              objectFit: "cover",
              position: "absolute",
              top: 0,
              left: 0,
            }}
            preview={{
              mask: (
                <Space direction="vertical" align="center" size={2}>
                  <EyeOutlined style={{ fontSize: 20 }} />
                  <span style={{ fontSize: 11 }}>مشاهده</span>
                </Space>
              ),
            }}
          />
        ) : (
          <div style={{ textAlign: "center" }}>
            {getFileIcon(file.fileName, isMobile ? 32 : 40)}
            <div style={{ marginTop: 6 }}>
              <Tag
                color={getFileTypeColor(file.fileName)}
                style={{ fontSize: 10, borderRadius: 4, padding: "0 6px" }}
              >
                {ext}
              </Tag>
            </div>
          </div>
        )}

        {/* File Type Badge */}
        <Tag
          color="processing"
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            borderRadius: 4,
            fontSize: 10,
            padding: "1px 6px",
            margin: 0,
          }}
        >
          {t("enums." + file.fileTypeName)}
        </Tag>
      </div>

      {/* Info Section */}
      <div style={{ padding: 12 }}>
        <Tooltip title={file.fileName}>
          <Text
            style={{
              fontSize: 12,
              display: "block",
              marginBottom: 10,
              color: "#595959",
              lineHeight: 1.4,
            }}
            ellipsis
          >
            {file.fileName}
          </Text>
        </Tooltip>

        {/* Actions */}
        <Flex gap={6}>
          <Button
            type="primary"
            size="small"
            icon={<CloudDownloadOutlined />}
            href={fileUrl}
            target="_blank"
            download
            style={{
              borderRadius: 6,
              flex: 1,
              height: 32,
              fontSize: 12,
            }}
          >
            دانلود
          </Button>

          {showDelete && (
            <Popconfirm
              title="حذف فایل"
              description="آیا از حذف این فایل اطمینان دارید؟"
              onConfirm={() => props.onDeleteGasProjectFile(file.id)}
              okText="بله"
              cancelText="خیر"
              placement="topRight"
            >
              <Button
                danger
                type="default"
                size="small"
                icon={<DeleteOutlined />}
                style={{
                  borderRadius: 6,
                  height: 32,
                  width: 32,
                }}
              />
            </Popconfirm>
          )}
        </Flex>
      </div>
    </Card>
  )
}

// Empty State Component
const EmptyState = () => (
  <div
    style={{
      textAlign: "center",
      padding: "32px 16px",
      background: "#fafafa",
      borderRadius: 12,
      border: "1px dashed #d9d9d9",
    }}
  >
    <FolderOpenOutlined
      style={{
        fontSize: 48,
        color: "#bfbfbf",
        marginBottom: 12,
      }}
    />
    <Title
      level={5}
      style={{ color: "#8c8c8c", marginBottom: 4, fontSize: 14 }}
    >
      فایلی یافت نشد
    </Title>
    <Text type="secondary" style={{ fontSize: 12 }}>
      هنوز فایلی آپلود نشده است
    </Text>
  </div>
)

const GridFiles = React.memo(gridProps => {
  const [selectedLinks, setSelectedLinks] = useState([])
  const [selectAll, setSelectAll] = useState(false)
  const [loading, setLoading] = useState(false)

  const { props } = gridProps
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const showDelete = props.showDelete ? props.showDelete : false
  const files = gridProps.lstGasProjectFiles || []

  // No files - show empty state
  if (files.length === 0) {
    return <EmptyState />
  }

  const handleDownloadLinks = async () => {
    try {
      setLoading(true)
      const response = await fetch(
        process.env.REACT_APP_API_URL + "/Users/GetPhysicalZipFileS3",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(selectedLinks ),
        }
      )
      if (!response.ok) {
        throw new Error("Network response was not ok")
      }
      const blob = await response.blob()
      const link = document.createElement("a")
      link.href = URL.createObjectURL(blob)
      link.download = "1001"  + selectedLinks[0].fileNumber + '.zip' || 'files.zip'
      link.click()
      URL.revokeObjectURL(link.href)
    } catch (error) {
      console.error("Failed to download files:", error)
    } finally {
      setLoading(false) // Set loading state to false when download completes (or fails)
    }
  }

  const handleSelectAll = e => {
    const checked = e.target.checked
    setSelectAll(checked)
    if (checked) {
      const allowedTypes = ['IdCard', 'BackFrontAssign', 'ApprovedPostalCode','DesignPlanAssign','Consent']
      const allLinks = files.filter(f=>allowedTypes.includes(f.fileTypeName)).map(file =>
      ({
        path:getUrlGasProjectFiles(
          file.folderName + "/" + file.fileName,
          file.gasProject.fileNumber
        ),
        fileNumber:file.gasProject.fileNumber
      })
      )
      setSelectedLinks(allLinks)
    } else {
      setSelectedLinks([])
    }
  }

  // Card Grid View (both mobile and desktop)
  return (
    <>
      <Row>
        <Button
          disabled={selectedLinks.length === 0}
          type="primary"
          onClick={handleDownloadLinks}
          loading={loading}
        >
          دانلود zip
        </Button>
        <Checkbox checked={selectAll} onChange={e => handleSelectAll(e)}>
          فایل
        </Checkbox>
      </Row>
      <Row gutter={[12, 12]}>
        {files.map(file => (
          <Col key={file.id} xs={12} sm={12} md={8} lg={8} xl={6}>
            <FileCard
              file={file}
              props={props}
              showDelete={showDelete}
              t={props.t}
              isMobile={isMobile}
            />
          </Col>
        ))}
      </Row>
    </>
  )
})

export default GridFiles
